import { reactive } from 'vue';
import { supabase } from '../supabase.js';
import { coreState } from './core.js';
import { useAuth } from './useAuth.js';

let notificationsChannel = null;

export const useUserStore = () => {

    const auth = useAuth(); // [NEW] Access Auth Store

    const state = reactive({
        isLoading: false,
        error: null
    });

    // --- Notifications ---
    async function fetchNotifications(userId) {
        if (!userId) return;

        // [FIX] Ensure Session Validity - bail out if session is invalid



        const { data, error } = await supabase
            .from('notifications')
            .select('*')
            .eq('user_id', userId)
            .neq('status', 'archived')
            .order('created_at', { ascending: false });

        if (error) {
            console.error("[UserStore] Notification Fetch Error:", error);
            // [FIX] Handle auth errors by logging out
            /* Auth error handled by interceptor */

            state.error = error;
            return;
        }

        data.forEach(n => {
            const note = {
                id: n.id,
                userId: n.user_id,
                type: n.type,
                category: n.category,
                title: n.title,
                message: n.message,
                time: n.created_at,
                status: n.status,
                actionLabel: n.action_label,
                actionUrl: n.action_url,
                payload: n.payload,
                icon: n.icon
            };
            coreState.notifications.set(n.id, note);
        });
    }

    function getNotifications(userId) {
        const list = [];
        for (const n of coreState.notifications.values()) {
            if (n.userId === userId) list.push(n);
        }
        return list.sort((a, b) => new Date(b.time) - new Date(a.time));
    }

    async function removeNotification(notificationId) {
        const { error } = await supabase
            .from('notifications')
            .update({ status: 'archived' })
            .eq('id', notificationId);

        if (error) {
            console.error("[UserStore] Failed to remove notification:", error);
            throw error;
        }

        coreState.notifications.delete(notificationId);
    }

    function subscribeToNotifications(userId) {
        if (!userId) return;

        if (notificationsChannel) {
            supabase.removeChannel(notificationsChannel);
        }

        notificationsChannel = supabase.channel('user_notifications')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'notifications',
                    filter: `user_id=eq.${userId}`
                },
                (payload) => {
                    if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
                        const n = payload.new;
                        if (n.status === 'archived') {
                            coreState.notifications.delete(n.id);
                        } else {
                            const note = {
                                id: n.id,
                                userId: n.user_id,
                                type: n.type,
                                category: n.category,
                                title: n.title,
                                message: n.message,
                                time: n.created_at,
                                status: n.status,
                                actionLabel: n.action_label,
                                actionUrl: n.action_url,
                                payload: n.payload,
                                icon: n.icon
                            };
                            coreState.notifications.set(n.id, note);
                        }
                    } else if (payload.eventType === 'DELETE') {
                        coreState.notifications.delete(payload.old.id);
                    }
                }
            )
            .subscribe((status) => {
                if (status === 'SUBSCRIBED') {
                    console.log('[UserStore] Subscribed to realtime notifications');
                }
            });
    }

    function unsubscribeFromNotifications() {
        if (notificationsChannel) {
            supabase.removeChannel(notificationsChannel);
            notificationsChannel = null;
        }
    }

    // --- Profile Getters ---
    async function fetchUser(userId) {
        if (!userId) return null;

        // Return existing if available (Double check if it's a full profile or stub?)
        // For now, assume if it exists, it's good enough or managed by Auth.
        if (coreState.users.has(userId)) {
            return coreState.users.get(userId);
        }

        state.isLoading = true;

        try {
            // 1. Fetch Profile
            const { data: profileData, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single();

            if (error) throw error;
            if (!profileData) return null;

            // 2. Fetch Shop Memberships (to determine Role/Title)
            const { data: members, error: memError } = await supabase
                .from('shop_members')
                .select('role, shop:shops(name)')
                .eq('user_id', userId)
                .eq('status', 'active'); // Only active memberships

            let primaryRole = 'Diver';
            if (members && members.length > 0) {
                // Simple priority: Owner > Manager > Staff > Member
                // Or just take the first one
                primaryRole = members[0].role;
            }

            // 3. Construct Model (Avoid circular dependency on Profile class if possible, or assume simple object)
            // We can re-use the Profile class if we import it, but let's conform to coreState structure.
            const userObj = {
                id: profileData.id,
                name: profileData.name || 'Anonymous',
                email: profileData.email, // Might be private?
                avatar: profileData.avatar,
                role: primaryRole,
                qualifications: profileData.qualifications || [],
                specialties: profileData.specialties || [],
                signupCompleted: profileData.signup_completed,
                stats: profileData.stats || { totalDives: 0 },
                historySummary: profileData.history_summary || { totalLogs: 0 }
            };

            // 4. Cache it
            coreState.users.set(userId, userObj);

            return userObj;

        } catch (err) {
            console.error("[UserStore] Failed to fetch user profile:", err);
            return null;
        } finally {
            state.isLoading = false;
        }
    }

    function getQualifications(userId) {
        const user = coreState.users.get(userId);
        return user?.qualifications || [];
    }

    function getSpecialties(userId) {
        const user = coreState.users.get(userId);
        return user?.specialties || [];
    }

    return {
        state,
        fetchNotifications,
        getNotifications,
        removeNotification,
        subscribeToNotifications,
        unsubscribeFromNotifications,
        fetchUser,
        getQualifications,
        getSpecialties
    };
};
